import { ExternalLink, FileX } from 'lucide-react';
import { CommentaryCard } from './CommentaryCard';
import { ExecutiveSummaryPanel } from './ExecutiveSummaryPanel';
import { MetricsGrid } from './MetricsGrid';
import { SentimentBadge } from './SentimentBadge';
import { SourceFooter } from './SourceFooter';
import type { CommentarySection, DocumentResult } from './types';

interface DocumentInsightsPanelProps {
  doc: DocumentResult;
}

const COMMENTARY_KEYS = [
  'revenue_commentary',
  'ebitda_commentary',
  'asset_efficiency_commentary',
  'industrial_performance_commentary',
] as const;

export function DocumentInsightsPanel({ doc }: DocumentInsightsPanelProps) {
  if (doc.status !== 'success' || !doc.insights) {
    return (
      <div className="py-14 flex flex-col items-center gap-3 text-center">
        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
          <FileX className="h-5 w-5 text-red-500" aria-hidden="true" />
        </div>
        <p className="text-sm font-semibold text-neutral-700">
          {doc.document_type.toUpperCase()} document unavailable
        </p>
        <p className="text-xs text-neutral-400 max-w-xs">
          This document could not be retrieved or analyzed. Try refreshing the analysis.
        </p>
      </div>
    );
  }

  const { insights, metadata } = doc;

  const commentaries = COMMENTARY_KEYS.reduce<CommentarySection[]>((acc, key) => {
    const c = insights[key];
    if (c) acc.push(c);
    return acc;
  }, []);

  const hasMetrics =
    !!insights.financial_metrics && Object.keys(insights.financial_metrics).length > 0;
  const hasPdf = !!metadata?.pdf_url && metadata.download_status !== 'failed';

  return (
    <div className="space-y-6">
      {/* Top bar: sentiment/confidence (left) + Open PDF (right) */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SentimentBadge
          sentiment={insights.sentiment}
          confidence={insights.extraction_confidence}
        />
        {hasPdf && (
          <a
            href={metadata!.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
            aria-label="Open source document PDF in new tab"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            Open PDF
          </a>
        )}
      </div>

      {/* Financial Metrics — surfaced early so key numbers are immediately visible */}
      {hasMetrics && <MetricsGrid metrics={insights.financial_metrics!} />}

      {/* Executive Summary */}
      {insights.overall_summary && (
        <ExecutiveSummaryPanel summary={insights.overall_summary} />
      )}

      {/* Commentary Cards */}
      {commentaries.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-3">
            Commentary &amp; Analysis
          </h3>
          <div className="grid gap-4 md:grid-cols-2" role="list" aria-label="Analysis commentary">
            {commentaries.map((c) => (
              <div key={c.title} role="listitem">
                <CommentaryCard commentary={c} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document Source */}
      <SourceFooter metadata={metadata} />
    </div>
  );
}
